import { useMemo, useState } from 'react'
import { Alert, Button, Card, Flex, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import {
  applyLocalList,
  byInstant,
  byNumber,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { JobTitleFormModal } from '@/features/hr/JobTitleFormModal'
import { fetchJobTitles, type JobTitle } from '@/features/hr/hrApi'

/**
 * Lavozimlar katalogi — `hr.job_title`.
 *
 * <h2>⚠️ O'CHIRISH TUGMASI YO'Q</h2>
 * `DELETE` yo'li backendda umuman yo'q va `employment_job_title_fk`
 * `RESTRICT` (`JobTitleService` javadoc'i). Sabab: lavozimni o'chirish
 * o'tgan tayinlash davrlarini NOMSIZ qoldirardi va maosh hisoboti
 * "2024-yil martda bu odam qaysi lavozimda edi" savoliga javob bera
 * olmasdi.
 *
 * O'rniga `active = false`. Jadvalda "nechta xodim" ustuni aynan shu
 * qaror uchun bor: `JobTitleView.personCount` javadoc'i — "panel
 * 'lavozimni o'chirsam bo'ladimi' degan savolga shu son bilan javob
 * oladi".
 *
 * <h2>⛔ SAHIFALASH YO'Q — SERVER UNI QO'LLAB-QUVVATLAMAYDI</h2>
 * `JobTitleController#list` YAGONA parametr oladi:
 * `@RequestParam(defaultValue = "false") boolean activeOnly`. `page`,
 * `size`, `sort`, `q` — YO'Q. Ya'ni javob doim BUTUN katalog.
 *
 * Shuning uchun bu yerda `DataTable` ISHLATILMAYDI: u serverdan sahifa
 * so'rashga qurilgan va uni mavjud bo'lmagan parametrlar bilan
 * chaqirish yolg'on shartnoma bo'lardi. Butun ro'yxatni olib mijozda
 * kesish esa ATAYLAB qilinmadi — egasi qarori (2026-08-20): sahifalash
 * bor deb ko'rsatib, aslida uni yasash ko'zbo'yamachilik.
 *
 * ⚠️ Qidiruv va saralash esa mijozda va bu YOLG'ON EMAS: massivda
 * butun ro'yxat turibdi, ya'ni "faqat joriy sahifa saralandi" degan
 * holatning o'zi yo'q (`shared/lib/localList.ts` javadoc'i).
 *
 * <h2>⚠️ SUKUT BO'YICHA FAQAT FAOL LAVOZIMLAR (egasi qarori 2026-08-20)</h2>
 * «Yopilganlar qoidasi»: ro'yxat amaldagi yozuvlarni ko'rsatadi, nofaoli
 * esa HOLAT FILTRI orqali olinadi. Shuning uchun filtrning bo'sh holati
 * YO'Q — u `active` dan boshlanadi va tanlagich tozalanmaydi. Tozalanadigan
 * tanlagich «Holat» degan yozuv bilan turib ro'yxatni jimgina toraytirardi.
 *
 * <h2>⚠️ «Faol» FILTRI SERVERGA BORADI, «Nofaol» esa YO'Q</h2>
 * Server faqat `activeOnly=true` ni biladi — "faqat nofaollar" degan
 * so'rov unda umuman yo'q. Shuning uchun birinchi holat serverga
 * uzatiladi (bekorga qator tashilmasin), ikkinchisi to'liq ro'yxat
 * ustida hisoblanadi. Sahifalash yo'q, ya'ni bu «joriy sahifani kesish»
 * emas: massivda butun katalog turibdi.
 *
 * <h2>⚠️ O'QISH `hr.person.read` BILAN</h2>
 * Alohida `hr.jobtitle.read` kodi ochilmagan
 * (`JobTitleController` javadoc'i): katalog xodim kartochkasi va
 * tayinlash oynasining bir qismi. YOZISH esa alohida
 * (`hr.jobtitle.write`).
 */
export function HrJobTitlesPage() {
  const { t } = useTranslation()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<JobTitle | null>(null)

  // ⚠️ Standart tartib — «oxirgi qo'shilgan birinchi» (egasi talabi).
  //    `createdAt` javobda BOR (`JobTitleView`), ya'ni tartib o'ylab
  //    topilgan emas.
  const list = useLocalListState({ defaultSort: { field: 'createdAt', order: 'descend' } })
  // ⚠️ Filtr qo'yilmagan holat = `active`, `null` EMAS (sinf javadoc'i).
  const activeFilter = list.filter('active') ?? 'active'
  const activeOnly = activeFilter === 'active'

  const titles = useQuery({
    queryKey: queryKeys.hr.jobTitles(activeOnly),
    queryFn: () => fetchJobTitles(activeOnly),
  })

  const rows = useMemo(() => titles.data ?? [], [titles.data])

  const visible = useMemo(
    () =>
      applyLocalList(rows, {
        search: list.search,
        searchFields: (row) => [row.name, row.code],
        filters: {
          // Serverga yuborilgan holat bu yerda QAYTA tekshirilmaydi —
          // faqat serverda yo'q shox (`nofaol`) hisoblanadi.
          active: (row, value) => (value === 'inactive' ? !row.active : true),
        },
        filterState: list.state.filters,
        comparators: {
          name: byText((row) => row.name),
          personCount: byNumber((row) => row.personCount),
          createdAt: byInstant((row) => row.createdAt),
        },
        sort: list.sort,
      }),
    [rows, list.search, list.state.filters, list.sort],
  )

  const columns: ColumnsType<JobTitle> = [
    {
      // Sahifalash yo'q — butun katalog bir ekranda, raqam `index` dan
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'name',
      title: t('hr.jobTitle.name'),
      ...sortColumn(list.sort, 'name'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong={row.active} type={row.active ? undefined : 'secondary'}>
            {seededName('jobTitles', row.code, row.name, t)}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.code}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'active',
      title: t('hr.jobTitle.active'),
      width: 140,
      render: (_, row) =>
        row.active ? (
          <Tag color="green">{t('hr.jobTitle.stateActive')}</Tag>
        ) : (
          <Tag>{t('hr.jobTitle.stateInactive')}</Tag>
        ),
    },
    {
      key: 'personCount',
      title: t('hr.jobTitle.personCount'),
      width: 200,
      ...sortColumn(list.sort, 'personCount'),
      render: (_, row) => t('hr.jobTitle.personCountValue', { count: row.personCount }),
    },
    {
      key: 'createdAt',
      title: t('common.createdAt'),
      width: 180,
      ...sortColumn(list.sort, 'createdAt'),
      // ⚠️ Ustun BEZAK emas: standart tartib aynan shu maydon bo'yicha.
      //    Ko'rsatmasak "nega bu qator tepada?" degan savol javobsiz
      //    qolardi. `null` — server vaqtni bermadi, eski degani EMAS.
      render: (_, row) =>
        row.createdAt === null ? (
          <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        ) : (
          <Tooltip title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Tooltip>
        ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('common.edit'),
              onClick: () => {
                setEditing(row)
                setFormOpen(true)
              },
              permission: 'hr.jobtitle.write',
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.hrJobTitles')}
        description={t('pageIntro.hrJobTitles')}
        extra={
          <Can permission="hr.jobtitle.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('hr.jobTitle.createTitle')}
            </Button>
          </Can>
        }
      />

      {titles.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(titles.error, t)}
        />
      ) : null}

      <Card title={t('hr.jobTitle.listTitle', { count: rows.length })}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('hr.jobTitle.noDeleteNote')}
        />

        {/* Qidiruv va filtr KO'RINADIGAN joyda: ustun voronkasi ichidagi
            boshqaruv amalda mavjud emas bilan barobar (etalon jadvalda
            o'lchangan). */}
        <Flex wrap gap="small" align="center" style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            value={list.searchText}
            onChange={(e) => list.setSearchText(e.target.value)}
            placeholder={t('hr.jobTitle.searchPlaceholder')}
            style={{ width: 260, maxWidth: '100%' }}
          />
          {/* ⚠️ `allowClear` YO'Q: bo'sh holat «hammasi» degan ma'no
              berardi, aslida esa sukut — faqat faollar. */}
          <Select<string>
            value={activeFilter}
            onChange={(value) => list.setFilter('active', value)}
            aria-label={t('listView.statusFilter')}
            style={{ width: 170 }}
            options={[
              { value: 'active', label: t('listView.active') },
              { value: 'inactive', label: t('listView.inactive') },
            ]}
          />
          {list.narrowed ? (
            <>
              <Typography.Text type="secondary">
                {t('listView.shown', { shown: visible.length, total: rows.length })}
              </Typography.Text>
              <Button type="link" size="small" onClick={list.reset}>
                {t('dataTable.clearFilters')}
              </Button>
            </>
          ) : null}
        </Flex>

        <Table<JobTitle>
          rowKey={(row) => row.id}
          columns={columns}
          dataSource={[...visible]}
          loading={titles.isPending}
          pagination={false}
          size="small"
          scroll={{ x: 900 }}
          onChange={localSortChange<JobTitle>(list.patch)}
          locale={{
            emptyText:
              // ⚠️ IKKI XIL BO'SHLIK ARALASHTIRILMAYDI: katalogning
              //    o'zi bo'sh bo'lsa "qo'shing" deyiladi, filtr hech
              //    narsa topmasa esa filtrni tozalash taklif etiladi.
              //    Bitta matn ikkinchisining o'rniga chiqsa operator
              //    mavjud ma'lumotni yo'q deb o'ylardi.
              rows.length > 0 && list.narrowed ? (
                <EmptyState
                  variant="no-data"
                  title={t('listView.noMatch')}
                  description={t('listView.noMatchHint', { total: rows.length })}
                  actionLabel={t('dataTable.clearFilters')}
                  onAction={list.reset}
                />
              ) : activeOnly ? (
                // ⚠️ UCHINCHI bo'shliq: server FAOLLARNI so'radi va
                //    bittasini ham topmadi. «Katalog bo'sh» deyish
                //    yolg'on bo'lardi — nofaollari turgan bo'lishi
                //    mumkin va ular bir bosishda ochiladi.
                <EmptyState
                  variant="no-data"
                  title={t('listView.activeOnlyEmpty')}
                  description={t('listView.activeOnlyEmptyHint')}
                  actionLabel={t('listView.activeOnlyEmptyAction')}
                  onAction={() => list.setFilter('active', 'inactive')}
                />
              ) : (
                <EmptyState
                  variant="no-data"
                  title={t('hr.jobTitle.empty')}
                  description={t('hr.jobTitle.emptyHint')}
                />
              ),
          }}
        />

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('listView.fullListNote')}
        </Typography.Text>
      </Card>

      <JobTitleFormModal
        open={formOpen}
        jobTitle={editing}
        onClose={() => setFormOpen(false)}
      />
    </>
  )
}
